import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { EditorCanvasProvider } from '../canvas/editor-canvas-provider';
import { ResolutionsProvider } from '../resolution/resolutions-provider';
import { useEditorCanvas } from '../canvas/hooks/use-editor-canvas';
import { useResolutions } from '../resolution/hooks/use-resolutions';
import { useCreativityEditor } from './hooks/use-creativity-editor';
import { useGroupingShortcuts } from '../grouping/hooks/use-grouping-shortcuts';
import { useModelComposition } from '../model/use-model-composition';
import { PlaybackProvider, usePlayback } from '../playback';
import { useCanvasInteractionLock } from '../canvas/hooks/use-canvas-interaction-lock';
import { EditorLayout } from './components/editor';
import { EditorTopBar } from './components/editor-top-bar';
import { ExportOverlay } from '../export/components/export-overlay';
import { ExportFailedModal } from '../export/components/export-failed-modal';
import { CanvasJSON } from '../canvas/types';
import { ResolutionConfig } from '../shared/types/binding-types';
import { CreativityEditorHandle, CreativityEditorProps } from '../shared/types/public';
import { EditorHostProvider } from '../shared/hooks/use-editor-host';
import { exportedVideoName } from '../shared/utils/exported-video-name';
import { MediaProvider } from '../media';
import { useMedia } from '../media';
import { PieceProvider, usePieceModel, usePieceStore } from '../model/piece-context';
import { pieceReducer } from '../model/piece-reducer';
import { normalizeModel } from '../model/normalize';
import { resolveBinding } from '../model/resolve-binding';
import type { PieceModel } from '../model/types';
import type { BindingType } from '../shared/types/binding-types';
import { getAudioLayers } from '../model/selectors';
import { useCanvasToModelSync } from '../model/sync/use-canvas-to-model-sync';
import { useTimelineEditorBridge } from '../timeline';
import { BrandKitProvider } from '../brand-kit';
import { UploadMediaProvider } from '../media/upload-media-provider';

const Inner = forwardRef<CreativityEditorHandle, CreativityEditorProps>((props, ref) => {
  const {
    initialCanvasJSON,
    initialPieceModel,
    isSaving,
    getExportFileName,
    onSave,
    onSaveAs,
    onReady,
    onLoadingChange,
    onExport,
    onBack
  } = props;
  const kind = props.kind ?? 'editor';

  const layerLabelResolver = props.kind === 'form-editor' ? props.layerLabelResolver : undefined;
  const resolveResolutionJson = props.kind === 'form-editor' ? props.resolveResolutionJson : undefined;

  const { canvas, loadProject, loadPiece, setLayerLabelResolver } = useEditorCanvas();
  const { isMediaLoading, getVideo } = useMedia();
  const { activeResolution } = useResolutions();
  const pieceStore = usePieceStore();
  const model = usePieceModel();
  const composition = useModelComposition();
  const { isPlaying, play, pause } = usePlayback();

  useCanvasInteractionLock(canvas, isPlaying || isMediaLoading);

  const { restoreEditingState } = useTimelineEditorBridge(canvas);

  const readCanvasModel = useCanvasToModelSync(canvas, {
    width: activeResolution.canvasWidth,
    height: activeResolution.canvasHeight,
    type: activeResolution.type,
    getVideo
  });

  // The binder mutates Fabric without touching the model, so read the canvas at save time.
  // Playback writes to those same objects, so undo it first or its frame leaks in as geometry.
  const getModel = useCallback((): PieceModel => {
    restoreEditingState();
    const canvasModel = readCanvasModel();
    const state = pieceStore.getState();
    const model = canvasModel ? pieceReducer(state, { type: 'syncFromCanvas', model: canvasModel }) : state;
    return normalizeModel(model);
  }, [restoreEditingState, readCanvasModel, pieceStore]);

  const applyBindingToModel = useCallback(
    (pieceId: string, bindingType: BindingType, value: unknown): boolean => {
      const resolved = resolveBinding(pieceStore.getState(), pieceId, bindingType, value);
      if (resolved === 'unhandled') return false;
      if (resolved !== 'skip')
        pieceStore.dispatch({ type: 'patchLayerContent', id: resolved.id, patch: resolved.patch });
      return true;
    },
    [pieceStore]
  );

  useEffect(() => {
    if (canvas) onReady?.();
  }, [canvas, onReady]);

  useEffect(() => {
    onLoadingChange?.(isMediaLoading);
  }, [isMediaLoading, onLoadingChange]);

  const defaultResolver = useCallback(
    async (resolution: ResolutionConfig): Promise<CanvasJSON> => {
      if (resolution.id === activeResolution.id && canvas) return canvas.toJSON() as CanvasJSON;
      return resolution.layout;
    },
    [canvas, activeResolution.id]
  );

  const handleExportComplete = useCallback(
    (blob: Blob) => {
      onExport?.({ kind: 'video', blob, fileName: exportedVideoName() });
    },
    [onExport]
  );

  const editor = useCreativityEditor({
    resolveResolutionJson: resolveResolutionJson ?? defaultResolver,
    getModel,
    getExportFileName,
    onExportComplete: onExport ? handleExportComplete : undefined
  });

  useGroupingShortcuts(editor.canvas, editor.transaction);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (!canvas || loadedRef.current) return;
    if (initialPieceModel) {
      loadedRef.current = true;
      loadPiece(initialPieceModel);
    } else if (initialCanvasJSON) {
      loadedRef.current = true;
      loadProject(initialCanvasJSON);
    }
  }, [canvas, initialPieceModel, initialCanvasJSON, loadPiece, loadProject]);

  useEffect(() => {
    if (!layerLabelResolver) return;
    setLayerLabelResolver(layerLabelResolver);
    return () => setLayerLabelResolver(null);
  }, [layerLabelResolver, setLayerLabelResolver]);

  useImperativeHandle(
    ref,
    () => ({
      loadProject: (json: CanvasJSON) => loadProject(json),
      loadPiece: (piece: PieceModel) => loadPiece(piece),
      getModel,
      applyBindingToModel,
      getCanvas: () => canvas,
      getComposition: () => composition,
      recordVideo: () => editor.recordVideo()
    }),
    [canvas, composition, loadProject, loadPiece, getModel, applyBindingToModel, editor.recordVideo]
  );

  const snapshotCanonicalJSON = useCallback((): CanvasJSON | null => {
    if (!canvas) return null;
    pause();
    restoreEditingState();
    const json = canvas.toJSON() as CanvasJSON;
    return { ...json, audioLayers: getAudioLayers(model) };
  }, [canvas, pause, model, restoreEditingState]);

  const handleSave = useCallback(() => {
    const json = snapshotCanonicalJSON();
    if (json) onSave?.(json);
  }, [onSave, snapshotCanonicalJSON]);

  const handleSaveAs = useCallback(() => {
    const json = snapshotCanonicalJSON();
    if (json) onSaveAs?.(json);
  }, [onSaveAs, snapshotCanonicalJSON]);

  const handlePreview = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <EditorTopBar
        onSave={handleSave}
        onNew={onSaveAs ? handleSaveAs : undefined}
        onBack={onBack}
        onPreview={handlePreview}
        onExport={editor.handleExport}
        isPlaying={isPlaying}
        isExporting={editor.isExportingAll || editor.isExportingVideo}
        isSaving={isSaving}
        isMediaLoading={isMediaLoading}
        showPreview={editor.hasTimelineContent}
        resolutions={editor.resolutions}
        activeResolution={editor.activeResolution}
        onResolutionChange={editor.setActiveResolutionId}
      />
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <EditorLayout kind={kind} showTimeline={editor.showTimeline} />
      </div>
      {editor.isExportingVideo && (
        <ExportOverlay
          progress={editor.progress}
          onCancel={editor.cancelVideoExport}
          recordingMode={editor.recordingMode}
        />
      )}
      {editor.exportNotice && (
        <ExportFailedModal
          title={editor.exportNotice.title}
          reason={editor.exportNotice.reason}
          onDismiss={editor.dismissExportNotice}
        />
      )}
    </div>
  );
});

Inner.displayName = 'CreativityEditorInner';

export const CreativityEditor = forwardRef<CreativityEditorHandle, CreativityEditorProps>((props, ref) => (
  <EditorHostProvider host={props.host}>
    <BrandKitProvider brandKit={props.brandKit}>
      <UploadMediaProvider uploadMedia={props.uploadMedia}>
        <MediaProvider>
          <ResolutionsProvider
            resolutions={props.resolutions}
            defaultResolutionId={props.defaultResolutionId}
            activeResolutionId={props.activeResolutionId}
            onActiveResolutionChange={props.onResolutionChange}
          >
            <PieceProvider>
              <EditorCanvasProvider>
                <PlaybackProvider>
                  <Inner {...props} ref={ref} />
                </PlaybackProvider>
              </EditorCanvasProvider>
            </PieceProvider>
          </ResolutionsProvider>
        </MediaProvider>
      </UploadMediaProvider>
    </BrandKitProvider>
  </EditorHostProvider>
));

CreativityEditor.displayName = 'CreativityEditor';
